import { Timeslot } from '@/shared/lib/timeslot/timeslot.types';
import { z } from 'zod';

/**
 * Timeslot Zod 스키마
 * ISO-8601_ts 형식의 문자열을 Timeslot으로 변환
 */
const timeslotSchema = z.string().transform((val): Timeslot => val as Timeslot);

// ============================================================================
// OrderStatus - Discriminated Union for Polymorphism
// ============================================================================

/**
 * SchedulingTiming 타입
 * 방문 일정 조율 타이밍
 */
export type SchedulingTiming = { type: 'Anytime' } | { type: 'AfterDays'; days: number };

/**
 * SuggestedVisitSchedule 타입
 * 제안된 방문 일정 (deprecated, backward compatibility)
 */
export interface SuggestedVisitSchedule {
  startTime: string; // HH:mm 형식 (LocalTime)
  endTime: string; // HH:mm 형식 (LocalTime)
}

/**
 * CancelReason 타입
 * 주문 취소 사유
 */
export type CancelReason =
  | '중복'
  | '부재 3회 초과'
  | '방문 거절'
  | '촬영 거절'
  | '가격만 궁금'
  | '재접수'
  | '이사 일정 미정'
  | '오주문'
  | '방문 가능 일정 미정'
  | '정보 오입력'
  | '짐 정리 미비'
  | '타업체 계약'
  | '기타'
  | '미파악'
  | 'RFQ 전환'
  | '방문 일정 조율 불가'
  | '(방문 중) 촬영 거절'
  | '(방문 중) 접수자 부재'
  | '견적 유효기한 경과'
  | '타업체 계약 - 가격'
  | '타업체 계약 - 신뢰'
  | '타업체 계약 - 지인 추천'
  | '타업체 계약 - 기타'
  | '타업체 계약 - 미파악';

/**
 * OrderStatus - Discriminated Union
 * 각 상태별로 다른 필드 구조를 가집니다.
 *
 * 참고: OrderView.visit 필드는 Order 엔티티의 preferredVisitSchedule을 매핑하며,
 * OrderStatus와는 별개로 모든 상태에서 존재합니다.
 */
export type OrderStatus =
  | {
      type: 'Received';
      // 참고: preferredVisitSchedule은 Order 엔티티의 속성이며 OrderView.visit으로 매핑됩니다.
    }
  | {
      type: 'Scheduling';
      counsel: string | null; // VisitCounselId (UUID)
      timing: SchedulingTiming;
      preferredVisitSchedules: Timeslot[]; // Scheduling 상태 전환 시 사용된 선호 일정
      suggestedVisitSchedules?: SuggestedVisitSchedule[]; // deprecated: backward compatibility
    }
  | {
      type: 'VisitAppointed';
      counsel: string; // VisitCounselId (UUID) - 필수
    }
  | {
      type: 'VisitCompleted';
      counsel: string; // VisitCounselId (UUID) - 필수
    }
  | {
      type: 'ClosedLost';
      counsel: string | null; // VisitCounselId (UUID)
      reason: CancelReason;
    }
  | {
      type: 'Unqualified';
      counsel: string | null; // VisitCounselId (UUID)
      reason: string;
      activeOrderId: string; // OrderId (UUID)
    }
  | {
      type: 'Requote';
      counsel: string | null; // VisitCounselId (UUID)
      reason: string;
      activeOrderId: string; // OrderId (UUID)
    };

// ============================================================================
// OrderView and Related Types
// ============================================================================

/**
 * House 타입
 * 이사 주소 및 공급면적 정보
 */
export interface House {
  location: string; // Location value class (String)
  supplyArea: number; // Pyeong value class (Int)
}

/**
 * MovingDetail 타입
 * 이사 상세 정보
 */
export interface MovingDetail {
  moveDate: string; // LocalDate (YYYY-MM-DD 형식)
  departure: House;
  arrival: House | null;
}

/**
 * DepartureLocationInfo 타입
 * 출발지의 도로명주소, 좌표, AI 추출 주소 상세 정보.
 * buildingName / dong / floorOrUnit 은 RegionAssignProcess 에서 AI 자동 추출 후 저장되며,
 * 상담 시작 전에도 존재할 수 있다 (없을 수도 있음).
 */
export interface DepartureLocationInfo {
  roadAddress: string;
  longitude: number;
  latitude: number;
  /** AI 자동기입: 건물명 */
  buildingName?: string | null;
  /** AI 자동기입: 동 번호/이름 (예: "102", "가", "B") */
  dong?: string | null;
  /** AI 자동기입: 층 또는 호수 */
  floorOrUnit?: { type: 'Floor' | 'Unit'; value: string } | null;
}

/**
 * OrderView 타입
 * 주문 조회 뷰
 *
 * 참고: visit 필드는 Order 엔티티의 preferredVisitSchedule을 매핑하며,
 * OrderStatus와 무관하게 모든 상태(Received, Scheduling, VisitAppointed 등)에서 존재합니다.
 */
export interface OrderView {
  id: string; // OrderId (UUID)
  misoOrderId: string; // MisoOrderId
  orderer: string; // CustomerId (UUID)
  ordererMisoCustomerId: string; // MisoCustomerId
  status: OrderStatus;
  moving: MovingDetail;
  visit: Timeslot[]; // Order.preferredVisitSchedule 매핑 (모든 상태에서 존재)
  orderedAt: string; // OffsetDateTime (ISO 8601)
  expiresAt: string; // OffsetDateTime (ISO 8601) - 필수
  ordererPhone?: string | null;
  assignedLegalRegionId?: string | null; // LegalRegionId
  assignedServiceRegionId?: string | null; // ServiceRegionId
  assignedDepartureLocation?: DepartureLocationInfo | null;
}

// ============================================================================
// Search Types
// ============================================================================

/**
 * 서버 API 스펙에 맞는 필터 타입 (id 없음, field 사용)
 * 서버는 value를 String?, values를 List<String>?로 받음
 */
export interface OrderSearchFilter {
  field: string;
  operator: string;
  value: string | null;
  values: string[] | null;
}

/**
 * 서버 API 스펙에 맞는 필터 그룹 타입 (id 없음)
 */
export interface OrderSearchFilterGroup {
  logic: 'AND' | 'OR';
  filters: readonly OrderSearchFilter[];
}

export interface OrderSearchQuery {
  filterGroups: OrderSearchFilterGroup[];
  /** 백엔드 허용: orderedAt, moveDate, id (createdAt 없음) */
  sortBy: 'orderedAt' | 'moveDate' | 'id';
  sortOrder: 'ASC' | 'DESC';
  pageSize: number;
  cursor?: string | null;
}

export interface OrderSearchView {
  items: OrderView[];
  hasNext: boolean;
  nextCursor: string | null;
  count: number;
}

// ============================================================================
// OrderEventView
// ============================================================================

/**
 * OrderEventViewItem 타입
 * 이벤트 + checkpoint 래퍼 (New 스펙)
 */
export interface OrderEventViewItem {
  event: OrderEvent;
  checkpoint: number;
}

/**
 * OrderEventView 타입
 * 주문 이벤트 조회 뷰
 */
export interface OrderEventView {
  orderId: string; // OrderId (UUID)
  events: OrderEventViewItem[];
}

/**
 * OrderEvent 타입
 * 주문 이벤트 (일반적인 이벤트 구조)
 */
export interface OrderEvent {
  id: string; // EventId
  eventType: string;
  occurredAt: string; // OffsetDateTime (ISO 8601)
  aggregateId: string; // OrderId (UUID)
  sequence: number;
  [key: string]: any; // 이벤트별 추가 필드
}

// ============================================================================
// Zod Schemas
// ============================================================================

/**
 * SchedulingTiming 스키마
 */
export const schedulingTimingSchema: z.ZodType<SchedulingTiming> = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Anytime'),
  }),
  z.object({
    type: z.literal('AfterDays'),
    days: z.number().int().positive(),
  }),
]);

/**
 * SuggestedVisitSchedule 스키마
 */
export const suggestedVisitScheduleSchema = z.object({
  startTime: z.string().regex(/^\d{2}:\d{2}$/, 'startTime은 HH:mm 형식이어야 합니다.'),
  endTime: z.string().regex(/^\d{2}:\d{2}$/, 'endTime은 HH:mm 형식이어야 합니다.'),
});

/**
 * CancelReason 스키마
 */
export const cancelReasonSchema = z.enum([
  '중복',
  '부재 3회 초과',
  '방문 거절',
  '촬영 거절',
  '가격만 궁금',
  '재접수',
  '이사 일정 미정',
  '오주문',
  '방문 가능 일정 미정',
  '정보 오입력',
  '짐 정리 미비',
  '타업체 계약',
  '기타',
  '미파악',
  'RFQ 전환',
  '방문 일정 조율 불가',
  '(방문 중) 촬영 거절',
  '(방문 중) 접수자 부재',
  '견적 유효기한 경과',
  '타업체 계약 - 가격',
  '타업체 계약 - 신뢰',
  '타업체 계약 - 지인 추천',
  '타업체 계약 - 기타',
  '타업체 계약 - 미파악',
]);

/**
 * OrderStatus 스키마 - Discriminated Union
 */
export const orderStatusSchema: z.ZodType<OrderStatus> = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Received'),
  }),
  z.object({
    type: z.literal('Scheduling'),
    counsel: z.string().uuid().nullable(),
    timing: schedulingTimingSchema,
    preferredVisitSchedules: z.array(timeslotSchema),
    suggestedVisitSchedules: z.array(suggestedVisitScheduleSchema).optional(),
  }),
  z.object({
    type: z.literal('VisitAppointed'),
    counsel: z.string().uuid(),
  }),
  z.object({
    type: z.literal('VisitCompleted'),
    counsel: z.string().uuid(),
  }),
  z.object({
    type: z.literal('ClosedLost'),
    counsel: z.string().uuid().nullable(),
    reason: cancelReasonSchema,
  }),
  z.object({
    type: z.literal('Unqualified'),
    counsel: z.string().uuid().nullable(),
    reason: z.string(),
    activeOrderId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('Requote'),
    counsel: z.string().uuid().nullable(),
    reason: z.string(),
    activeOrderId: z.string().uuid(),
  }),
]);

/**
 * House 스키마
 */
export const houseSchema = z.object({
  location: z.string(),
  supplyArea: z.number().int().nonnegative(),
});

/**
 * DepartureLocationInfo 스키마
 */
export const departureLocationInfoSchema = z.object({
  roadAddress: z.string(),
  longitude: z.number(),
  latitude: z.number(),
  buildingName: z.string().nullable().optional(),
  dong: z.string().nullable().optional(),
  floorOrUnit: z
    .object({ type: z.enum(['Floor', 'Unit']), value: z.string() })
    .nullable()
    .optional(),
});

/**
 * MovingDetail 스키마
 * moveDate는 LocalDate (YYYY-MM-DD 형식)
 */
export const movingDetailSchema = z.object({
  moveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'moveDate는 YYYY-MM-DD 형식이어야 합니다.'),
  departure: houseSchema,
  arrival: houseSchema.nullable(),
});

/**
 * OrderView 스키마
 */
export const orderViewSchema = z.object({
  id: z.string().uuid(),
  misoOrderId: z.string(),
  orderer: z.string().uuid(),
  ordererMisoCustomerId: z.string(),
  status: orderStatusSchema,
  moving: movingDetailSchema,
  visit: z.array(timeslotSchema),
  orderedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  ordererPhone: z.string().nullable().optional(),
  assignedLegalRegionId: z.string().nullable().optional(),
  assignedServiceRegionId: z.string().nullable().optional(),
  assignedDepartureLocation: departureLocationInfoSchema.nullable().optional(),
});

/**
 * OrderSearchFilter 스키마
 */
export const orderSearchFilterSchema = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.string().nullable(),
  values: z.array(z.string()).nullable(),
});

/**
 * OrderSearchFilterGroup 스키마
 */
export const orderSearchFilterGroupSchema = z.object({
  logic: z.enum(['AND', 'OR']),
  filters: z.array(orderSearchFilterSchema),
});

/**
 * OrderSearchQuery 스키마
 */
export const orderSearchQuerySchema = z.object({
  filterGroups: z.array(orderSearchFilterGroupSchema),
  sortBy: z.enum(['orderedAt', 'moveDate', 'id']),
  sortOrder: z.enum(['ASC', 'DESC']),
  pageSize: z.number().int().min(1).max(100),
  cursor: z.string().nullish(),
});

/**
 * OrderSearchView 스키마
 */
export const orderSearchViewSchema = z.object({
  items: z.array(orderViewSchema),
  hasNext: z.boolean(),
  nextCursor: z.string().nullable(),
  count: z.number().int().nonnegative(),
});

/**
 * OrderEvent 스키마
 */
export const orderEventSchema = z
  .object({
    id: z.string(),
    eventType: z.string(),
    occurredAt: z.string().datetime(),
    aggregateId: z.string().uuid(),
    sequence: z.number().int().nonnegative(),
  })
  .passthrough(); // 추가 필드 허용

/**
 * OrderEventViewItem 스키마
 */
export const orderEventViewItemSchema = z.object({
  event: orderEventSchema,
  checkpoint: z.number().int(),
});

/**
 * OrderEventView 스키마
 */
export const orderEventViewSchema = z.object({
  orderId: z.string().uuid(),
  events: z.array(orderEventViewItemSchema),
});

// ============================================================================
// Type Inference from Schemas
// ============================================================================

export type HouseInput = z.infer<typeof houseSchema>;
export type DepartureLocationInfoInput = z.infer<typeof departureLocationInfoSchema>;
export type MovingDetailInput = z.infer<typeof movingDetailSchema>;
export type OrderStatusInput = z.infer<typeof orderStatusSchema>;
export type OrderViewInput = z.infer<typeof orderViewSchema>;
export type OrderSearchFilterInput = z.infer<typeof orderSearchFilterSchema>;
export type OrderSearchFilterGroupInput = z.infer<typeof orderSearchFilterGroupSchema>;
export type OrderSearchQueryInput = z.infer<typeof orderSearchQuerySchema>;
export type OrderSearchViewInput = z.infer<typeof orderSearchViewSchema>;
export type OrderEventInput = z.infer<typeof orderEventSchema>;
export type OrderEventViewItemInput = z.infer<typeof orderEventViewItemSchema>;
export type OrderEventViewInput = z.infer<typeof orderEventViewSchema>;
